import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { API } from '@/api'
import DoubleArrowUpIcon from '@/assets/icons/double-arrow-up.svg?react'
import RechargeIcon from '@/assets/icons/recharge.svg?react'
import { Divider } from '@/components'
import { Button } from '@/components/base/Button'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { PrivilegeType } from '@/consts/team'
import { Team } from '@/models/team'
import { useTeamBalance } from '@/pages/billing/hooks/useTeamBalance'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatDatePreset, formatPrice } from '@/utils/formatter'

import type { TeamPlan } from './CreateTeamModal'
import { TeamRenewModal } from './TeamRenewModal'

interface ManagePlanSuccessModalProps {
  open: boolean
  onClose: () => void
  team: Team
  selectedPlan: TeamPlan
}

export function ManagePlanSuccessModal({
  open,
  onClose,
  team,
  selectedPlan,
}: ManagePlanSuccessModalProps) {
  const { t } = useTranslation('team')
  const userStore = useUserStore()
  const teamStore = useTeamStore()
  const [showRenewModal, setShowRenewModal] = useState(false)
  const [localTeam, setLocalTeam] = useState<Team>(team)

  const { teamBalance, isLoading } = useTeamBalance(team.id, open)

  // Sync props changes
  useEffect(() => {
    setLocalTeam(team)
  }, [team])

  const handleRenewClick = () => {
    setShowRenewModal(true)
  }

  const handleRechargeClick = async () => {
    const primaryTeamData = teamStore.primaryTeamTokenData

    // Check if switching back to primary team with valid token
    if (
      primaryTeamData &&
      primaryTeamData.teamId === team.id &&
      primaryTeamData.userName === team.user.name &&
      primaryTeamData.expiryTime > Date.now()
    ) {
      const { LOGIN_TOKEN_COOKIE_EXPIRY_DAYS } = await import('@/utils/auth')
      teamStore.setTeamContext(primaryTeamData, LOGIN_TOKEN_COOKIE_EXPIRY_DAYS)
      sessionStorage.setItem('__team_switching__', '1')
      window.location.href = '/team/billing'
      return
    }

    // Request new 15-minute token
    const res = await API.getTeamToken({
      privilege_type: PrivilegeType.TeamToken,
      source_id: team.id,
    })

    const { TEAM_TOKEN_EXPIRY_MS } = await import('@/utils/auth')
    const expiryTime = Date.now() + TEAM_TOKEN_EXPIRY_MS
    teamStore.setTeamContext({
      token: `Bearer ${res.token}`,
      teamId: team.id,
      teamResID: team.resID,
      expiryTime,
      userName: team.user.name,
    })

    sessionStorage.setItem('__team_switching__', '1')
    window.location.href = '/team/billing'
  }

  return (
    <>
      <Modal open={open} onClose={onClose} className="w-[800px]">
        <Modal.Header>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h6">{t('managePlan.successTitle')}</span>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="space-y-4">
            {/* Description */}
            <p className="t-sb text-primary">
              <Trans
                i18nKey="managePlan.successDescription"
                ns="team"
                values={{ planName: selectedPlan.name }}
                components={{
                  highlight: <span className="text-highlight" />,
                }}
              >
                Your team plan has been successfully changed to{' '}
                <span className="text-highlight">{selectedPlan.name} Plan</span>
              </Trans>
            </p>

            {/* Current Plan Section */}
            <div className="bg-neutral-50 rounded-lg p-4 space-y-6">
              {/* Section Header */}
              <div className="flex gap-2 items-center">
                <h3 className="t-xs text-tertiary flex-shrink-0 text-nowrap">
                  {t('managePlan.currentPlan')}
                </h3>
                <Divider className="my-0" />
              </div>

              {/* Team ID & Team Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="t-xs text-secondary mb-1">{t('success.teamId')}</div>
                  <div className="t-sb text-primary">{localTeam.resID}</div>
                </div>
                <div>
                  <div className="t-xs text-secondary mb-1">{t('success.teamName')}</div>
                  <div className="t-sb text-primary">{localTeam.name}</div>
                </div>
              </div>

              {/* Team Plan & Expires on */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="t-xs text-secondary mb-1">{t('checkout.teamPlan')}</div>
                  <div className="t-sb text-primary">{selectedPlan.name} Plan</div>
                </div>
                <div>
                  <div className="t-xs text-secondary mb-1">{t('managePlan.expiresOn')}</div>
                  <div className="flex items-center gap-1">
                    <span className="t-sb text-primary">
                      {localTeam.orderDeadline
                        ? formatDatePreset(localTeam.orderDeadline, 'day')
                        : '-'}
                    </span>
                    <button
                      className="flex items-center gap-1 text-highlight"
                      onClick={handleRenewClick}
                    >
                      <DoubleArrowUpIcon className="w-4 h-4" />
                      <span className="t-sb">{t('managePlan.renew')}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Team Credit */}
              <div>
                <div className="t-xs text-secondary mb-1">{t('managePlan.teamCredit')}</div>
                <div className="flex items-center gap-1">
                  <span className="t-sb text-primary">
                    {isLoading ? (
                      <Loading className="h-4 w-4" />
                    ) : (
                      formatPrice(teamBalance, false, teamBalance > 100 ? 'Credits' : 'Credit')
                    )}
                  </span>
                  <button
                    className="flex items-center gap-1 text-highlight"
                    onClick={handleRechargeClick}
                  >
                    <RechargeIcon className="w-4 h-4" />
                    <span className="t-sb">{t('managePlan.recharge')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex justify-end">
            <Button type="secondary" onClick={onClose} className="w-[88px]">
              {t('managePlan.ok')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {showRenewModal && (
        <TeamRenewModal
          open={showRenewModal}
          team={localTeam}
          onClose={() => setShowRenewModal(false)}
          onSuccess={async () => {
            setShowRenewModal(false)
            // Refetch team data to update expiration date
            try {
              const teamsRes = await API.getTeamList({ page: 1, per_page: 100 })
              const teams = teamsRes.teams.map((item) => new Team(item))
              const newTeam = teams.find((t) => t.resID === team.resID)
              if (newTeam) {
                setLocalTeam(newTeam)
              }
            } catch (error) {
              console.error('Failed to fetch updated team info:', error)
            }
            userStore.getUserInfo()
          }}
        />
      )}
    </>
  )
}
